/**
 * Screen Permission System — Single source of truth for screen access control.
 *
 * Model: Role preset + per-user override
 *   - Each role has a default set of screens
 *   - Each user can have per-screen overrides in agents.screen_permissions JSONB
 *   - Override TRUE → kullanıcı bu ekrana erişebilir (rol default'ı atlayarak)
 *   - Override FALSE → kullanıcı bu ekrana erişemez
 *   - Override yoksa (null/undefined) → rol default'ı geçerli
 */

// ─── Screen Registry ──────────────────────────────────────────────────────
// Tüm sayfalar burada tek kaynak olarak listelenir.
// `area` UI'da grup başlığı için kullanılır.

export const SCREENS = {
  // ─── Admin (11) ─────────────────────────────────────────────────────────
  AdminDashboard: { label: 'Dashboard', area: 'admin' },
  AdminLeads: { label: 'Kontakte', area: 'admin' },
  AdminTasks: { label: 'Aufgaben', area: 'admin' },
  AdminOrders: { label: 'Aufträge', area: 'admin' },
  AdminProducts: { label: 'Produkte', area: 'admin' },
  AdminStockMovements: { label: 'Bestandsbewegungen', area: 'admin' },
  AdminSuppliers: { label: 'Lieferanten', area: 'admin' },
  AdminAgents: { label: 'Benutzerverwaltung', area: 'admin' },
  AdminReports: { label: 'Berichte', area: 'admin' },
  AdminInbox: { label: 'Angebots-Eingang', area: 'admin' },
  AdminLeadHunter: { label: 'Lead Hunter', area: 'admin' },
  AdminAgentPerformance: { label: 'Performance', area: 'admin' },
  AdminPdfSettings: { label: 'PDF Einstellungen', area: 'admin' },
  AdminEwkKategorien: { label: 'EWK-Kategorien', area: 'admin' },
  AdminShopifyOrders: { label: 'Shopify Bestellungen', area: 'admin' },
  AdminIntegrationen: { label: 'Integrationen', area: 'admin' },
  AdminKundenportal: { label: 'Kundenportal', area: 'admin' },
  AdminEinstellungen: { label: 'Einstellungen', area: 'admin' },

  // ─── Agent (6) ──────────────────────────────────────────────────────────
  AgentTasks: { label: 'Aufgaben', area: 'agent' },
  AgentLeads: { label: 'Kontakte', area: 'agent' },
  AgentOrders: { label: 'Aufträge', area: 'agent' },
  AgentCatalog: { label: 'Katalog', area: 'agent' },
  AgentProfile: { label: 'Profil', area: 'agent' },
  AgentReport: { label: 'Bericht', area: 'agent' },

  // ─── Warehouse (6) ──────────────────────────────────────────────────────
  WarehouseDashboard: { label: 'Depo', area: 'warehouse' },
  WarehouseDispo: { label: 'Dispo', area: 'warehouse' },
  WarehouseInventur: { label: 'Inventur', area: 'warehouse' },
  WarehouseWareneingang: { label: 'Wareneingang', area: 'warehouse' },
  WarehouseWarenausgang: { label: 'Warenausgang', area: 'warehouse' },
  WarehouseSuppliers: { label: 'Lieferanten', area: 'warehouse' },

  // ─── Shipping (1) ───────────────────────────────────────────────────────
  ShippingDashboard: { label: 'Versand', area: 'shipping' },

  // ─── Buchhaltung ────────────────────────────────────────────────────────
  BuchhaltungRechnungen: { label: 'Rechnungen', area: 'buchhaltung' },
  BuchhaltungMahnungen: { label: 'Mahnungen', area: 'buchhaltung' },
  BuchhaltungGutschriften: { label: 'Gutschriften', area: 'buchhaltung' },
  BuchhaltungDashboard: { label: 'Finanzen', area: 'buchhaltung' },
  BuchhaltungJournal: { label: 'Journal', area: 'buchhaltung' },
  BuchhaltungKonten: { label: 'Kontenplan', area: 'buchhaltung' },
  BuchhaltungBerichte: { label: 'Fibu-Berichte', area: 'buchhaltung' },
  BuchhaltungBank: { label: 'Bank & Zahlungen', area: 'buchhaltung' },
  BuchhaltungAusgaben: { label: 'Ausgaben', area: 'buchhaltung' },
  BuchhaltungWiederkehrend: { label: 'Wiederkehrende Ausgaben', area: 'buchhaltung' },
  BuchhaltungAnlagen: { label: 'Anlagen & AfA', area: 'buchhaltung' },
  BuchhaltungAbschluss: { label: 'Jahresabschluss', area: 'buchhaltung' },
};

export const AREA_LABELS = {
  admin: 'Administration',
  agent: 'Vertrieb',
  warehouse: 'Lager',
  shipping: 'Versand',
  buchhaltung: 'Buchhaltung',
};

// ─── Role Defaults ────────────────────────────────────────────────────────
// Her rolün override yoksa erişebileceği ekranların listesi.
// Admin tüm ekranlara erişir.

const ALL_SCREENS = Object.keys(SCREENS);

export const ROLE_DEFAULTS = {
  admin: ALL_SCREENS, // her şey
  agent: [
    'AgentTasks',
    'AgentLeads',
    'AgentOrders',
    'AgentCatalog',
    'AgentProfile',
    'AgentReport',
  ],
  warehouse: [
    'WarehouseDashboard',
    'WarehouseDispo',
    'WarehouseInventur',
    'WarehouseWareneingang',
    'WarehouseWarenausgang',
    'WarehouseSuppliers',
    'AgentProfile', // bottom nav'da profile var
  ],
  shipping: [
    'ShippingDashboard',
    'AgentProfile',
  ],
  buchhaltung: [
    'BuchhaltungRechnungen',
    'BuchhaltungMahnungen',
    'BuchhaltungGutschriften',
    'BuchhaltungDashboard',
    'BuchhaltungJournal',
    'BuchhaltungKonten',
    'BuchhaltungBerichte',
    'BuchhaltungBank',
    'BuchhaltungAusgaben',
    'BuchhaltungWiederkehrend',
    'BuchhaltungAnlagen',
    'BuchhaltungAbschluss',
    'AgentProfile',
  ],
};

// ─── Resolver ─────────────────────────────────────────────────────────────

/**
 * Kullanıcının erişebileceği ekranları çözer.
 * Role default + override'ları birleştirir.
 *
 * @param {Object|null} user - { role, screen_permissions }
 * @returns {Object<string, boolean>} - Ekran adı → erişilebilir mi
 */
export function resolvePermissions(user) {
  if (!user) return {};

  const role = (user.role || '').toLowerCase();
  const defaultScreens = ROLE_DEFAULTS[role] || [];

  // Default map: rol default'ı true, diğerleri false
  const result = {};
  for (const screen of ALL_SCREENS) {
    result[screen] = defaultScreens.includes(screen);
  }

  // Override'ları uygula
  let overrides = user.screen_permissions;
  if (typeof overrides === 'string') {
    try {
      overrides = JSON.parse(overrides);
    } catch {
      overrides = null;
    }
  }
  if (overrides && typeof overrides === 'object') {
    for (const [screen, allowed] of Object.entries(overrides)) {
      if (screen in result) {
        result[screen] = !!allowed;
      }
    }
  }

  return result;
}

/**
 * Kestirme yardımcı — kullanıcı belirli bir ekrana erişebilir mi?
 */
export function canAccess(user, screenName) {
  if (!user) return false;
  // Admin her zaman her şey (defansif — rol default'ı zaten verir)
  if ((user.role || '').toLowerCase() === 'admin') return true;
  const perms = resolvePermissions(user);
  return !!perms[screenName];
}

/**
 * Belirli bir alana ait ekranların listesi (UI gruplaması için)
 */
export function getScreensByArea(area) {
  return Object.entries(SCREENS)
    .filter(([, meta]) => meta.area === area)
    .map(([key, meta]) => ({ key, ...meta }));
}
